import React, { useEffect, useState } from 'react'
import Header from './Header'
import Footer from './Footer'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'
import favicon from '/static/cg-favicon.png'
import StoryblokService from '../utils/storyblok-service'
import { CookiesProvider } from 'react-cookie'
import GclidCookie from 'src/utils/GclidCookie'

import {
  organizationSchema,
  cloudConnectSchema,
  psnConnectSchema,
  managedWanSchema,
  remoteAccessSchema,
  dataCentreConnectivitySchema,
  healthConnectSchema,
  fwaasSchema,
  swgSchema,
  wafSchema,
  blogPostSchema,
  vpnSCSchema,
} from '../components/Script'
export default function Layout({
  children,
  location,
  metaData,
  isEditor,
  page,
}) {
  function extractKeywords(text) {
    // !text || text === " "
    if (!text) return []
    const wordsArray = text
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .split(/\s+/) // Split by whitespace

    // Create a map to store word frequencies
    const wordFrequency = {}
    const exclusion = [
      // Common stop words
      'i',
      'me',
      'my',
      'myself',
      'we',
      'our',
      'ours',
      'ourselves',
      'you',
      'your',
      'yours',
      'yourself',
      'yourselves',
      'he',
      'him',
      'his',
      'himself',
      'she',
      'her',
      'hers',
      'herself',
      'it',
      'its',
      'itself',
      'they',
      'them',
      'their',
      'theirs',
      'themselves',
      'what',
      'which',
      'who',
      'whom',
      'this',
      'that',
      'these',
      'those',
      'am',
      'is',
      'are',
      'was',
      'were',
      'be',
      'been',
      'being',

      // Articles, prepositions, auxiliary verbs
      'the',
      'a',
      'an',
      'and',
      'or',
      'but',
      'if',
      'because',
      'as',
      'until',
      'while',
      'of',
      'at',
      'by',
      'for',
      'with',
      'about',
      'against',
      'between',
      'into',
      'through',
      'during',
      'before',
      'after',
      'above',
      'below',
      'to',
      'from',
      'up',
      'down',
      'in',
      'out',
      'on',
      'off',
      'over',
      'under',

      // Modal verbs and question words
      'can',
      'will',
      'shall',
      'would',
      'should',
      'could',
      'may',
      'might',
      'must',
      'why',
      'how',
      'did',
      'do',
      'does',
      'has',
      'have',
      'had',

      // Conjunctions and interjections
      'so',
      'then',
      'also',
      'too',
      'just',
      'not',
      'no',
      'yes',
      'very',
      'really',
    ]

    wordsArray.forEach(word => {
      if (!exclusion.includes(word)) {
        wordFrequency[word] = (wordFrequency[word] || 0) + 1
      }
    })

    const sortedWords = Object.keys(wordFrequency).sort(
      (a, b) => wordFrequency[b] - wordFrequency[a],
    )

    return sortedWords.slice(0, 5)
  }

  // Usage example

  const { settings } = useStaticQuery(graphql`
    query Settings {
      settings: allStoryblokEntry(
        filter: { field_component: { eq: "settings" } }
      ) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
    }
  `)
  let parsedSetting = {}
  let parsedSettingOrphan = {}

  settings.edges.forEach(edge => {
    const content = JSON.parse(edge.node.content)

    if (edge.node.name === 'OrphanSettings') {
      parsedSettingOrphan = { ...content, content }
    } else {
      parsedSetting = { ...content, content }
    }
  })

  let pathname = typeof location !== 'undefined' ? location.pathname : ''

  const schemaMap = {
    '/': organizationSchema,
    '/services/cloud-connect/': cloudConnectSchema,
    '/services/connectivity/cloud-connectivity/aws-connectivity/': cloudConnectSchema,
    '/services/connectivity/cloud-connectivity/': cloudConnectSchema,
    '/knowledge-centre/articles/exploring-dora-the-digital-operational-resilience-act-dora/': cloudConnectSchema,
    '/services/psn-connect/': psnConnectSchema,
    '/services/managed-wan/': managedWanSchema,
    '/services/remote-access/': remoteAccessSchema,
    '/services/data-centre-connectivity/': dataCentreConnectivitySchema,
    '/services/health-connect/': healthConnectSchema,
    '/services/fwaas/': fwaasSchema,
    '/services/vpn-site-connectivity/': vpnSCSchema,
    '/services/swg/': swgSchema,
    '/services/waf/': wafSchema,
    '/services/connectivity/psn-connect/': psnConnectSchema,
    '/services/connectivity/managed-wan/': managedWanSchema,
    '/services/connectivity/remote-access/': remoteAccessSchema,
    '/services/connectivity/data-centre-connectivity/': dataCentreConnectivitySchema,
    '/services/connectivity/health-connect/': healthConnectSchema,
    '/services/security/fwaas/': fwaasSchema,
    '/services/connectivity/vpn-site-connectivity/': vpnSCSchema,
    '/services/security/swg/': swgSchema,
    '/services/security/waf/': wafSchema,
  }

  const text = metaData?.content?.meta?.title
  const keywords = extractKeywords(text)

  const schemaToInject = pathname.startsWith('/articles/')
    ? blogPostSchema(
        metaData?.content?.meta?.title ?? 'Post Title',
        metaData?.content?.description ?? 'post description.',
        metaData?.author?.AuthorName ?? '',
        metaData?.first_published_at ? metaData?.first_published_at : '',
        metaData?.content?.featured_image?.filename ??
          'https://www.cloudgateway.co.uk/static/sample-image.jpg',
        metaData?.location?.href ??
          'https://www.cloudgateway.co.uk/blog/sample-post',
        keywords.join(', '),
      )
    : schemaMap[pathname] || null

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.6.0/css/all.min.css"
        ></link>
      </Helmet>
      {isEditor ? (
        <>
          <Helmet
            script={[
              {
                src: `//app.storyblok.com/f/storyblok-latest.js?t=${StoryblokService.token}`,
                type: 'text/javascript',
              },
            ]}
          />
          <Helmet
            script={[
              {
                innerHTML: `var StoryblokCacheVersion = '${StoryblokService.getCacheVersion()}';`,
                type: 'text/javascript',
              },
            ]}
          />
        </>
      ) : null}
      <Helmet>
        <link rel="icon" href={favicon} />
        <script
          src="https://kit.fontawesome.com/b1c70f5e3d.js"
          crossorigin="anonymous"
        ></script>
        <link
          rel="stylesheet"
          href="https://use.typekit.net/dzu5gtl.css"
        ></link>
        <script src="https://go.cloudgateway.co.uk/l/956103/2023-06-13/5d895/956103/1686663290Gk1OU9CE/seamless.parent.js"></script>
        {schemaToInject && (
          <script type="application/ld+json">
            {JSON.stringify(schemaToInject)}
          </script>
        )}
      </Helmet>
      {page === 'OrphanPage' ? (
        <Header settings={parsedSettingOrphan} type="orphan" />
      ) : (
        <Header settings={parsedSetting} />
      )}
      <CookiesProvider>
        <GclidCookie />
        <main>{children}</main>
      </CookiesProvider>
      {page === 'OrphanPage' ? (
        <Footer settings={parsedSettingOrphan} type="orphan" />
      ) : (
        <Footer settings={parsedSetting} />
      )}
    </>
  )
}
